import React, { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useRouter } from "next/router"
import _isEmpty from "lodash/isEmpty"
import moment from "moment"
import _get from "lodash/get"
import { getConfig } from "@/constants/config"
import { getProductsById } from "@/components/cart/cartHelper"
import useMyServicei18n from "@/i18n/useMyServices"
import { logout, selectAuthState } from "@/store/features/authSlice"
import MyServicesView from "@/components/MyServices/v1/MyServices.view"
import { setMenu } from "@/utils/orders"
import {
  getShortenedUrl,
  getSortItemBasedOnURL,
  replaceSearchUrl,
} from "@/utils/helper"
import { getServices } from "@/store/features/accountSlice"
import MyGenericFiller from "@/components/MyGenericFiller"

const MyServices = props => {
  const {
    isAuth,
    user,
    access_token: accessToken,
  } = useSelector(selectAuthState)
  const router = useRouter()
  const { data: componentProp } = props
  const staticTexts = useMyServicei18n({
    componentProp,
  })
  const selectedSortItem = getSortItemBasedOnURL(staticTexts, {
    name: staticTexts.newToOld,
    query: "new",
  })
  const [servicelist, setServicelist] = useState({
    active: [],
    past: [],
  })
  const [menuItems, setMenuItems] = useState([])
  const [config, setInitialConfig] = useState({})
  const [selectedSort, setSelectedSort] = useState(selectedSortItem)
  const [selectedTag, setSelectedTag] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loader, setLoader] = useState(true)
  const info = useRef({})
  const dispatch = useDispatch()
  const { newToOld, oldToNew, AToZ, ZToA } = staticTexts

  const sorts = [
    { name: newToOld, query: "new" },
    { name: oldToNew, query: "old" },
    { name: AToZ, query: `atoz` },
    { name: ZToA, query: "ztoa" },
  ]

  useEffect(() => {
    getConfig().then(async config => {
      setInitialState(config)
      const locale = _get(config, "internationalization.locale", "")
      info.current = {
        locale: locale,
      }
    })
    window.scrollTo({
      top: 1,
      behavior: "smooth",
    })
  }, [])

  useEffect(() => {
    if (!_isEmpty(config) && accessToken) {
      setMenuItems(setMenu(config, staticTexts))
      getServiceData()
    }
  }, [config, accessToken])

  useEffect(() => {
    if (selectedTag) {
      const tag = selectedTag === "active" ? "active" : "past"
      onSort(selectedSort, tag)
    }
  }, [selectedTag])

  const setInitialState = async config => {
    const accountSettingsPath = await getShortenedUrl(
      _get(config, "general.accountSettingsPath", "")
    )
    const myFavoritesPagePath = await getShortenedUrl(
      _get(config, "general.myFavoritesPagePath", "")
    )
    const orderHistoryPath = await getShortenedUrl(
      _get(config, "general.orderHistoryPath", "")
    )
    const myProductsPath = await getShortenedUrl(
      _get(config, "general.myProductsPath", "")
    )
    const myServicesPath = await getShortenedUrl(
      _get(config, "general.myServicesPath", "")
    )
    const orderDetailsUrl = _get(config, "general.orderDetailsPath", "")
    const continueShopUrl = _get(config, "general.continueShopPath", "")
    const currencySign = _get(config, "internationalization.currencySign", "")
    setInitialConfig({
      accountSettingsPath,
      myFavoritesPagePath,
      orderHistoryPath,
      myProductsPath,
      myServicesPath,
      orderDetailsUrl,
      continueShopUrl,
      currencySign,
    })
  }

  const redirect = url => {
    router.push(url)
  }

  const signOut = () => {
    dispatch(logout())
  }

  const getServiceData = async () => {
    if (accessToken) {
      dispatch(getServices())
        .unwrap()
        .then(async service => {
          const lineItems = service[0]?.lineItems ?? []
          const productData =
            lineItems?.length > 0 &&
            (await getProductsById(
              lineItems?.map(lineItem => lineItem.productId)
            ))
          const data = lineItems.reduce(
            (p, c) => {
              const expirationDate = moment(c.custom.fields.expirationDate)
              const purchasedDate = moment(c.custom.fields.lastPurchasedOn)
              const masterVariant = productData?.find(
                v => v.id === c.productId
              )?.masterVariant
              const obj = {
                brandName: _get(
                  masterVariant?.attributes.find(
                    attr => attr.name === "CustomerFacingBrand"
                  ),
                  "value",
                  ""
                ),
                expirationDate: expirationDate.isValid()
                  ? expirationDate.format("MM/DD/YYYY")
                  : false,
                lastPurchasedOn: purchasedDate.format("MM/DD/YYYY"),
                image: masterVariant?.images[0]?.url,
                name: c.name["en-US"],
              }

              if (
                expirationDate.isValid() &&
                moment().isAfter(expirationDate)
              ) {
                p.past.push(obj)
              } else {
                p.active.push(obj)
              }

              return p
            },
            { past: [], active: [] }
          )
          const newestToOldestDataActiveServices = data.active.sort(function (
            a,
            b
          ) {
            return new Date(b.lastPurchasedOn) - new Date(a.lastPurchasedOn)
          })
          const tag = data.length ? "past" : "active"
          setServicelist({ ...data, active: newestToOldestDataActiveServices })
          setSelectedTag(tag)
          setIsLoading(false)
        })
        .finally(() => setLoader(false))
    }
  }

  const onSort = (selectedSort, tag) => {
    const selectedObj = tag === "active" ? servicelist.active : servicelist.past
    let sortedData = []

    if (selectedSort.query === "atoz") {
      sortedData = selectedObj.sort((a, b) => a.name.localeCompare(b.name))
    } else if (selectedSort.query === "new") {
      sortedData = selectedObj.sort(
        (a, b) => new Date(b.lastPurchasedOn) - new Date(a.lastPurchasedOn)
      )
    } else if (selectedSort.query === "old") {
      sortedData = selectedObj.sort(
        (a, b) => new Date(a.lastPurchasedOn) - new Date(b.lastPurchasedOn)
      )
    } else {
      sortedData = selectedObj.sort((a, b) => b.name.localeCompare(a.name))
    }

    setServicelist({
      ...servicelist,
      [tag]: sortedData,
    })
    setSelectedSort(selectedSort)
    replaceSearchUrl("sort", selectedSort.query)
  }
  if (loader) {
    return <MyGenericFiller data={componentProp} title="myServices" />
  }

  return (
    <>
      <MyServicesView
        i18n={staticTexts}
        servicelist={servicelist}
        showDropdown={true}
        sorts={sorts}
        onSort={onSort}
        continueShoppingData={componentProp}
        isAuth={isAuth}
        user={user}
        logout={signOut}
        data={menuItems}
        redirect={redirect}
        selectedSort={selectedSort}
        isLoading={isLoading}
      />
    </>
  )
}

export default MyServices
